import { isClerkAPIResponseError, useSignIn, useSignUp } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { EmailCodeFactor, SignInFirstFactor } from "@clerk/types";
import { PageContainer } from "../../components/PageContainer";
import { ContentContainer } from "../../components/ContentContainer";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/Button";
import { GoogleSignInButton } from "./SignIn";
import { Divider } from "../../components/Divider";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../components/ui/input-otp";
import { useMe } from "../../lib/data/user";
import * as z from "zod";
import _ from "lodash";

function AlternativeSignIn() {
  const location = useLocation();
  return (
    <div className="m-auto mt-4 flex max-w-[400px] flex-col items-center justify-center gap-2 text-center">
      <Divider className="mt-2 w-full" />
      <span className="text-sm">or</span>
      <GoogleSignInButton intent="branded" previousUrl={location.pathname} />
    </div>
  );
}

function useRedirectIfSignedIn() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  useEffect(() => {
    if (!!me) {
      navigate("/dashboard");
    }
  }, [me]);
}

const signUpSchema = z.object({
  firstName: z.string().min(1, "First name is required."),
  lastName: z.string().min(1, "Last name is required."),
  email: z.string().email({ message: "Enter a valid email." }).min(1, "Email is required."),
});
export default function OTPSignUp() {
  useRedirectIfSignedIn();
  const [error, setError] = useState<string | undefined>();
  const { isLoaded, signUp, setActive } = useSignUp();
  const [isVerifying, setVerifying] = useState<boolean>(false);
  const [form, setForm] = useState<z.infer<typeof signUpSchema>>({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [code, setCode] = useState<string>("");
  const navigate = useNavigate();
  async function handleSubmit() {
    setError(undefined);
    const parsed = signUpSchema.safeParse(form);
    if (!parsed.success) {
      setError(
        _.first(parsed.error.errors)?.message ?? "Please check your form entries and try again.",
      );
      return;
    }
    if (!isLoaded) return null;
    try {
      await signUp.create({
        firstName: parsed.data.firstName,
        lastName: parsed.data.lastName,
        emailAddress: parsed.data.email,
      });
      await signUp.prepareEmailAddressVerification();
      setVerifying(true);
    } catch (err) {
      if (isClerkAPIResponseError(err)) {
        setError(err.errors.map(e => e.longMessage).join(" "));
      } else {
        setError("Error occurred. Please try again.");
      }
    }
  }

  async function handleVerification() {
    setError(undefined);
    if (!isLoaded) return null;
    try {
      const signUpAttempt = await signUp.attemptEmailAddressVerification({
        code,
      });
      if (signUpAttempt.status === "complete") {
        await setActive({
          session: signUpAttempt.createdSessionId,
        });
        navigate("/signed-in");
      } else {
        setError("Signup verification failed. Please try again.");
      }
    } catch (err) {
      if (isClerkAPIResponseError(err)) {
        setError(err.errors.map(e => e.longMessage).join(" "));
      } else {
        setError("Verification failed. Please try again.");
      }
    }
  }
  const content = isVerifying ? (
    <form
      onSubmit={handleVerification}
      className="m-auto flex max-w-[400px] flex-col items-center justify-center gap-4 text-center"
    >
      <h1 className="text-2xl font-bold">Verify</h1>
      <label htmlFor="code">Enter your verification code</label>
      <InputOTP
        value={code}
        id={code}
        name={code}
        maxLength={6}
        onChange={c => setCode(c)}
        onComplete={handleVerification}
      >
        <InputOTPGroup>
          <InputOTPSlot index={0} />
          <InputOTPSlot index={1} />
          <InputOTPSlot index={2} />
          <InputOTPSlot index={3} />
          <InputOTPSlot index={4} />
          <InputOTPSlot index={5} />
        </InputOTPGroup>
      </InputOTP>
      <Button
        type="button"
        size="md"
        intent="green"
        label="Verify"
        onClick={handleVerification}
        className="w-full"
      />
    </form>
  ) : (
    <form className="m-auto flex max-w-[400px] flex-col items-center justify-center gap-4 text-center">
      <h1 className="text-2xl font-bold">Sign up</h1>
      <label className="sr-only" htmlFor="email">
        Enter Email Address
      </label>
      <Input
        value={form.firstName}
        id="firstName"
        name="firstName"
        placeholder="First name"
        onChange={e => setForm(f => ({ ...f, firstName: e.currentTarget.value }))}
      />
      <Input
        value={form.lastName}
        id="lastName"
        name="lastName"
        placeholder="Last name"
        onChange={e => setForm(f => ({ ...f, lastName: e.currentTarget.value }))}
      />
      <Input
        value={form.email}
        id="email"
        name="email"
        type="email"
        placeholder="Email address"
        onChange={e => setForm(f => ({ ...f, email: e.currentTarget.value }))}
      />
      <Button
        type="button"
        size="md"
        intent="green"
        label="Continue"
        onClick={handleSubmit}
        className="w-full"
      />
    </form>
  );

  return (
    <PageContainer>
      <ContentContainer className="text-center">
        {content}
        {error && (
          <div className="m-auto w-full py-5 text-center text-sm text-red-500">{error}</div>
        )}

        <div className="pt-4 text-sm text-gray-600">
          Already have an account?{" "}
          <Link
            className="font-semibold text-gray-800 transition-all hover:text-green"
            to="/sign-in"
          >
            Sign In
          </Link>
        </div>
        <AlternativeSignIn />
      </ContentContainer>
    </PageContainer>
  );
}

export function OTPSignIn() {
  useRedirectIfSignedIn();
  const [error, setError] = useState<string | undefined>();
  const { isLoaded, signIn, setActive } = useSignIn();
  const [verifying, setVerifying] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const navigate = useNavigate();

  async function handleSubmit() {
    if (!isLoaded || !email) return null;
    try {
      const { supportedFirstFactors } = await signIn.create({
        identifier: email,
      });
      const isEmailCodeFactor = (factor: SignInFirstFactor): factor is EmailCodeFactor => {
        return factor.strategy === "email_code";
      };
      const factor: SignInFirstFactor | undefined = supportedFirstFactors.find(isEmailCodeFactor);
      if (factor) {
        await signIn.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: factor.emailAddressId,
        });
        setVerifying(true);
      }
    } catch (err) {
      if (isClerkAPIResponseError(err)) {
        setError(err.errors.map(e => e.longMessage).join(" "));
      } else {
        setError("Sign in failed. Are you sure you have an account already?");
      }
    }
  }

  async function handleVerification() {
    setError(undefined);
    if (!isLoaded) return null;
    try {
      const signInAttempt = await signIn.attemptFirstFactor({
        strategy: "email_code",
        code,
      });
      if (signInAttempt.status === "complete") {
        await setActive({
          session: signInAttempt.createdSessionId,
        });
        navigate("/signed-in");
      } else {
        setError("Verification failed. Please try again.");
      }
    } catch (err) {
      if (isClerkAPIResponseError(err)) {
        setError(err.errors.map(e => e.longMessage).join(" "));
      } else {
        setError("Verification failed. Please try again.");
      }
    }
  }

  const content = verifying ? (
    <form
      onSubmit={handleVerification}
      className="m-auto flex max-w-[400px] flex-col items-center justify-center gap-4 text-center"
    >
      <h1 className="text-2xl font-bold">Verify</h1>
      <label htmlFor="code">Enter your verification code</label>
      <InputOTP
        value={code}
        id={code}
        name={code}
        maxLength={6}
        onChange={c => setCode(c)}
        onComplete={handleVerification}
      >
        <InputOTPGroup>
          <InputOTPSlot index={0} />
          <InputOTPSlot index={1} />
          <InputOTPSlot index={2} />
          <InputOTPSlot index={3} />
          <InputOTPSlot index={4} />
          <InputOTPSlot index={5} />
        </InputOTPGroup>
      </InputOTP>
      <Button
        type="button"
        size="md"
        intent="green"
        label="Verify"
        onClick={handleVerification}
        className="w-full"
      />
    </form>
  ) : (
    <form className="m-auto flex max-w-[400px] flex-col items-center justify-center gap-4 text-center">
      <h1 className="text-2xl font-bold">Sign in</h1>
      <label className="sr-only" htmlFor="email">
        Enter Email Address
      </label>
      <Input
        value={email}
        id="email"
        name="email"
        type="email"
        placeholder="Enter email address..."
        onChange={e => setEmail(e.target.value)}
      />
      <Button
        type="button"
        size="md"
        intent="green"
        label="Continue"
        onClick={handleSubmit}
        className="w-full"
      />
    </form>
  );

  return (
    <PageContainer>
      <ContentContainer className="text-center">
        {content}
        {error && (
          <div className="m-auto w-full py-5 text-center text-sm text-red-500">{error}</div>
        )}
        <div className="pt-4 text-sm text-gray-600">
          Not registered?{" "}
          <Link
            className="font-semibold text-gray-800 transition-all hover:text-green"
            to="/sign-up"
          >
            Sign up
          </Link>
        </div>
        <AlternativeSignIn />
      </ContentContainer>
    </PageContainer>
  );
}
